// Schreibmaschinen-Effekt für gestreamte Antworten.
//
// Der Server liefert Text in unregelmäßigen Stücken. Statt ihn ruckartig
// anzuhängen, gibt der Composable ihn gleichmäßig je Frame aus und holt bei
// Rückstand schneller auf, damit die Anzeige nie weit hinter dem Stream
// zurückbleibt. Bei `prefers-reduced-motion` erscheint der Text sofort.

import { onBeforeUnmount, ref, type Ref, watch } from 'vue'

const MIN_CHARS_PER_FRAME = 2
const CATCH_UP_DIVISOR = 24

export function prefersReducedMotion (): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * @param target  vollständiger bisher empfangener Text
 * @param active  true solange gestreamt wird; false → Rest sofort ausgeben
 */
export function useTypewriter (target: Ref<string>, active: Ref<boolean>) {
  const displayed = ref(active.value ? '' : target.value)
  let frame = 0

  const stop = () => {
    if (frame) {
      cancelAnimationFrame(frame)
    }
    frame = 0
  }

  const tick = () => {
    frame = 0
    const full = target.value
    if (!full.startsWith(displayed.value)) {
      // Der Zieltext wurde ersetzt (z. B. finaler Inhalt ohne Vorschlagszeile).
      displayed.value = full
      return
    }
    const pending = full.length - displayed.value.length
    if (pending <= 0) {
      return
    }
    const chars = Math.max(MIN_CHARS_PER_FRAME, Math.ceil(pending / CATCH_UP_DIVISOR))
    displayed.value = full.slice(0, displayed.value.length + chars)
    if (displayed.value.length < full.length) {
      frame = requestAnimationFrame(tick)
    }
  }

  const schedule = () => {
    if (!active.value || prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
      stop()
      displayed.value = target.value
      return
    }
    if (!frame) {
      frame = requestAnimationFrame(tick)
    }
  }

  watch(target, schedule)
  watch(active, isActive => {
    if (!isActive) {
      stop()
      displayed.value = target.value
    }
  })

  onBeforeUnmount(stop)

  return { displayed }
}
